import { createContext, useContext, useState, type CSSProperties, type FormEvent } from "react";
import { Link } from "react-router-dom";
import type { PublicDealsRetailer } from "../../config/publicDeals";
import type { PublicPreviewDeal } from "../../services/PublicPreviewService";
import type { ProductListItem } from "../../types/Product";
import { getProductImageUrl } from "../../utils/imageUtils";
import { getProductStatus } from "../ProductDealBadges";
import ProductTileImage from "./ProductTileImage";
import { getGuestDealDiscount, getGuestDealPrice } from "../guest-deals/GuestDealsUi.utils";

/**
 * Colour tokens from the DealHawk app (approved web feed design). Values are CSS
 * variables defined in src/index.css (`:root` = light, `.dark` = dark), so inline
 * styles follow the footer Light/Dark toggle. Never append alpha suffixes to these.
 */
export const HF = {
  font: "-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
  /** Brand fill (buttons/badges with white text). */
  brand: "var(--hf-brand)",
  /** Brand-coloured text/strokes on page surfaces. */
  brandText: "var(--hf-brand-text)",
  bg: "var(--hf-bg)",
  ink: "var(--hf-ink)",
  text2: "var(--hf-text2)",
  text3: "var(--hf-text3)",
  muted: "var(--hf-muted)",
  border: "var(--hf-border)",
  hairline: "var(--hf-hairline)",
  /** Product photo tile; stays light in dark mode (photos have white backgrounds). */
  tile: "var(--hf-tile)",
  page: "var(--hf-page)",
  /** Raised surface: cards, inputs, sheets. */
  surface: "var(--hf-surface)",
  /** Soft section fill (hero panel, info boxes). */
  surface2: "var(--hf-surface-2)",
  hover: "var(--hf-hover)",
  cream: "var(--hf-cream)",
  divider: "var(--hf-divider)",
  chipBorder: "var(--hf-chip-border)",
  violet: "var(--hf-violet)",
  violetSoft: "var(--hf-violet-soft)",
  violetBorder: "var(--hf-violet-border)",
  store: "var(--hf-store)",
  price: "var(--hf-price)",
  green: "var(--hf-green)",
  discount: "var(--hf-discount)",
  red: "var(--hf-red)",
  heart: "var(--hf-heart)",
  amberBg: "var(--hf-amber-bg)",
  amberSoft: "var(--hf-amber-soft)",
  amberBorder: "var(--hf-amber-border)",
  amberPill: "var(--hf-amber-pill)",
  amberText: "var(--hf-amber-text)",
  amberInk: "var(--hf-amber-ink)",
  /** Ink-coloured button fill; inverts to a light button in dark mode. */
  inkBtn: "var(--hf-ink-btn)",
  onInkBtn: "var(--hf-on-ink-btn)",
  skeleton: "var(--hf-skeleton)",
  chevron: "var(--hf-chevron)",
  header: "var(--hf-header)",
  /** Behind product images that sit directly on a card (white light, light grey dark). */
  photo: "var(--hf-photo)",
  /** Locked-deal / unlock button fill (white text). */
  locked: "var(--hf-locked)",
  /** Body copy a shade softer than ink. */
  body: "var(--hf-body)",
  dashed: "var(--hf-dashed)",
  creamRow: "var(--hf-cream-row)",
  creamHover: "var(--hf-cream-hover)",
  creamBorder: "var(--hf-cream-border)",
  creamText: "var(--hf-cream-text)",
} as const;

function parseHex(hex: string): [number, number, number] | null {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return null;
  return [0, 2, 4].map((i) => parseInt(m[1].slice(i, i + 2), 16)) as [number, number, number];
}

function relativeLuminance([r, g, b]: [number, number, number]): number {
  const channel = (v: number) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}

/**
 * A brand/chain colour used as text: unchanged in light mode; in dark mode mixed toward white
 * just enough to read on the near-black page (navy, black and dark red brand colours otherwise
 * vanish). `--hf-dark` is 0 in light mode and 1 in dark mode (src/index.css).
 */
export function liftOnDark(color: string): string {
  const rgb = parseHex(color);
  if (!rgb) return color;
  // Smallest sRGB mix toward white (4% steps) that reaches a readable luminance on #0B0B0D.
  let lift = 0;
  while (lift < 80 && relativeLuminance(rgb.map((v) => v + (255 - v) * (lift / 100)) as [number, number, number]) < 0.32) {
    lift += 4;
  }
  if (lift === 0) return color;
  return `color-mix(in srgb, ${color} calc(100% - var(--hf-dark) * ${lift}%), #FFFFFF)`;
}

/** "Walmart.com" for the big-box online feeds, the brand name for the rest. */
export function retailerLabel(retailer: PublicDealsRetailer): string {
  if (retailer.displayName) return retailer.displayName;
  return retailer.name.endsWith(" Online") ? retailer.domain : retailer.shortName;
}

const LOCAL_NAMES: Record<string, string> = {
  walmart: "Walmart",
  home_depot: "HD",
  lowes: "Lowe’s",
  target: "Target",
  costco: "Costco",
  bestbuy: "Best Buy",
  best_buy: "Best Buy",
  dollar_general: "Dollar General",
  tractor_supply: "Tractor Supply",
  northern_tool: "Northern Tool",
  samsclub: "Sam's Club",
};

export function localChainName(retailer: string): string {
  const key = retailer.trim().toLowerCase();
  if (LOCAL_NAMES[key]) return LOCAL_NAMES[key];
  return key.split(/[_\s-]+/).filter(Boolean).map((w) => w[0].toUpperCase() + w.slice(1)).join(" ");
}

/** Some retailer feeds send names with HTML entities ("Inseam, &nbsp;Sizes"). */
export function cleanName(name: string): string {
  return name
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&quot;/gi, '"')
    .replace(/&#0?39;|&apos;/gi, "'")
    .replace(/&#(\d+);/g, (_, code) => String.fromCharCode(Number(code)))
    .replace(/\s+/g, " ")
    .trim();
}

const money = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2, maximumFractionDigits: 2 });

// ---------- icons (inline so they match the design strokes exactly) ----------

export function LockIcon({ size = 13, color = "currentColor" }: { size?: number; color?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.6" strokeLinecap="round" aria-hidden="true">
      <rect x="5" y="11" width="14" height="10" rx="2" />
      <path d="M8 11V8a4 4 0 0 1 8 0v3" />
    </svg>
  );
}

export function SearchIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden="true" className="flex-shrink-0">
      <circle cx="11" cy="11" r="7" />
      <path d="M20 20l-3.5-3.5" />
    </svg>
  );
}

export function ChevronDownIcon({ size = 16, up = false }: { size?: number; up?: boolean }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="flex-shrink-0">
      <path d={up ? "M18 15l-6-6-6 6" : "M6 9l6 6 6-6"} />
    </svg>
  );
}

export function GlobeIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden="true">
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h18M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18" />
    </svg>
  );
}

function HeartIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 20s-7-4.4-9-8.6C1.6 8.4 3.6 5 7 5c2 0 3.3 1.1 5 3 1.7-1.9 3-3 5-3 3.4 0 5.4 3.4 4 6.4-2 4.2-9 8.6-9 8.6z" />
    </svg>
  );
}

function PackageIcon() {
  return (
    <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#D4D1DC" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21 8l-9-5-9 5v8l9 5 9-5V8z" />
      <path d="M3 8l9 5 9-5M12 13v8" />
    </svg>
  );
}

// ---------- sharing ----------

/** Opens the page's share sheet for a deal; absent where sharing is off. */
export const HomeFeedShareContext = createContext<((product: ProductListItem, storeLabel: string) => void) | null>(null);

function ShareIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 3v12M7 8l5-5 5 5" />
      <path d="M5 13v6a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-6" />
    </svg>
  );
}

function ShareDealButton({ product, storeLabel, size }: { product: ProductListItem; storeLabel: string; size: "card" | "tile" | "row" }) {
  const share = useContext(HomeFeedShareContext);
  if (!share || !product.productKey) return null;
  // On a card the button floats over the photo's top-right corner, so the
  // price row keeps its full width on phones.
  const placement = size === "card"
    ? "absolute right-2.5 top-2.5 z-[1] h-8 w-8 shadow-[0_1px_3px_var(--hf-shadow-sm)]"
    : size === "tile"
      ? "h-8 w-8 shadow-[0_1px_3px_var(--hf-shadow-sm)]"
      : "h-9 w-9";
  return (
    <button
      type="button"
      onClick={() => share(product, storeLabel)}
      aria-label={`Share ${product.name}`}
      className={`flex flex-shrink-0 items-center justify-center rounded-full border bg-[var(--hf-surface)] transition hover:bg-[var(--hf-heart-soft)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#8B5CF6] ${placement}`}
      style={{ borderColor: HF.border, color: HF.violet }}
    >
      <ShareIcon />
    </button>
  );
}

// ---------- card pieces ----------

const pillBase: CSSProperties = {
  position: "absolute",
  height: 21,
  padding: "0 7px",
  borderRadius: 999,
  display: "flex",
  alignItems: "center",
  fontSize: 11,
  fontWeight: 800,
  letterSpacing: "0.3px",
  color: "#FFFFFF",
  pointerEvents: "none",
};

/** Sale red: a fill that always takes white text, and theme-aware text. */
const SALE_RED_FILL = "#D7262E";
const SALE_RED_TEXT = "var(--hf-sale-red)";

const cardClass = "group relative flex h-full flex-col gap-1.5 text-left";
/** Gold "67% OFF" tab hanging off the image's left edge on online cards. */
const offTab: CSSProperties = {
  position: "absolute",
  left: 0,
  bottom: 10,
  height: 26,
  padding: "0 11px 0 9px",
  borderRadius: "0 999px 999px 0",
  background: "#FFD24A",
  color: "#170B26",
  fontSize: 12.5,
  fontWeight: 900,
  letterSpacing: "-0.1px",
  display: "flex",
  alignItems: "center",
  pointerEvents: "none",
};

const LOCKED_INK = "#170B26";

const tagCardClass =
  "group relative flex h-full flex-col overflow-hidden rounded-[18px] border bg-[var(--hf-surface)] text-left shadow-[0_1px_2px_var(--hf-shadow-sm)] transition-shadow duration-200 hover:shadow-[0_10px_24px_var(--hf-shadow)]";

export function ProductFeedCard({
  product,
  storeLabel,
  onOpen,
  onSave,
}: {
  product: ProductListItem;
  storeLabel: string;
  onOpen: () => void;
  onSave: () => void;
}) {
  const price = getGuestDealPrice(product);
  const discount = getGuestDealDiscount(product);
  const msrp = Number(product.msrp) || 0;
  const status = getProductStatus(product);
  const [imageFailed, setImageFailed] = useState(false);
  const src = getProductImageUrl(product.image, "400");
  const label = `${product.name}${price > 0 ? `, ${money.format(price)}` : ""}${discount > 0 ? `, ${discount}% off` : ""}`;

  return (
    <article className="group relative flex h-full flex-col gap-1.5 text-left">
      {/* Photo tile: the product sized to a standard area, % off, save and share on top */}
      <div className="relative aspect-square w-full overflow-hidden rounded-[16px] sm:rounded-[18px]" style={{ background: HF.tile }}>
        <button
          type="button"
          onClick={onOpen}
          aria-label={label}
          className="absolute inset-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#8B5CF6]"
        >
          {src && !imageFailed ? <ProductTileImage src={src} onError={() => setImageFailed(true)} /> : <span className="flex h-full items-center justify-center"><PackageIcon /></span>}
        </button>
        {discount > 0 && (
          <span className="pointer-events-none absolute left-2 top-2 flex h-6 items-center rounded-[7px] px-2 text-[11.5px] font-extrabold text-white sm:left-2.5 sm:top-2.5 sm:h-[26px] sm:text-[12.5px]" style={{ background: SALE_RED_FILL }}>
            {discount}% off
          </span>
        )}
        <span className="absolute right-2 top-2 hidden group-hover:block group-focus-within:block sm:right-2.5 sm:top-2.5">
          <ShareDealButton product={product} storeLabel={storeLabel} size="tile" />
        </span>
        <button
          type="button"
          onClick={onSave}
          aria-label={`Save ${product.name}`}
          className="absolute bottom-2 right-2 flex h-9 w-9 items-center justify-center rounded-full bg-[var(--hf-surface)] shadow-[0_2px_6px_var(--hf-shadow)] transition hover:bg-[var(--hf-heart-soft)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#8B5CF6] sm:bottom-2.5 sm:right-2.5"
          style={{ color: HF.heart }}
        >
          <HeartIcon />
        </button>
      </div>
      <button type="button" onClick={onOpen} tabIndex={-1} aria-hidden="true" className="flex flex-col gap-1 text-left">
        <span className="flex min-w-0 items-baseline gap-1.5 pt-1">
          <span className="text-[18px] font-extrabold tabular-nums tracking-[-0.4px] sm:text-[20px]" style={{ color: price > 0 ? SALE_RED_TEXT : HF.ink }}>
            {price > 0 ? money.format(price) : "See price"}
          </span>
          {msrp > price && price > 0 && (
            <span className="truncate text-[12.5px] tabular-nums sm:text-[13px]" style={{ color: HF.muted }}>
              reg <span className="line-through">{money.format(msrp)}</span>
            </span>
          )}
        </span>
        <span className="line-clamp-2 h-9 text-[13.5px] leading-[18px] sm:h-10 sm:text-[14.5px] sm:leading-5" style={{ color: HF.ink }}>{cleanName(product.name)}</span>
        <span className="truncate text-[12px] font-semibold sm:text-[12.5px]" style={{ color: status === "new" ? HF.green : status === "lowest" ? HF.violet : HF.text3 }}>
          {status === "new" ? "New today" : status === "lowest" ? "Lowest price yet" : `${storeLabel} · online`}
        </span>
      </button>
    </article>
  );
}

const lockedCardStyle: CSSProperties = {};

export function discountBandPill(band: string): string {
  return `-${band.replace(/\s*off\s*$/i, "").trim()}`;
}

export function LockedDealCard({
  deal,
  zip,
  to,
  introLabel,
  onClick,
  example = false,
}: {
  deal: PublicPreviewDeal;
  zip: string;
  to: string;
  introLabel: string;
  onClick: () => void;
  /** A real find from another area, shown when we have nothing for the shopper's own ZIP. */
  example?: boolean;
}) {
  const chain = localChainName(deal.retailer);
  const [imageFailed, setImageFailed] = useState(false);
  const src = getProductImageUrl(deal.image, "400");
  return (
    <Link
      to={to}
      onClick={onClick}
      className={`${tagCardClass} focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#8B5CF6]`}
      style={{ borderColor: HF.border, color: HF.ink, textDecoration: "none" }}
      aria-label={`In-store clearance near ${zip}: ${deal.name}, ${deal.discountBand}. Unlock for ${introLabel} first month`}
      data-testid="locked-deal-card"
    >
      <span className="relative flex aspect-square w-full items-center justify-center overflow-hidden" style={{ background: HF.photo }}>
        {src && !imageFailed ? (
          <img
            src={src}
            alt=""
            loading="lazy"
            onError={() => setImageFailed(true)}
            className="absolute inset-[11%] h-[78%] w-[78%] object-contain transition-transform duration-300 group-hover:scale-[1.04]"
          />
        ) : (
          <PackageIcon />
        )}
        <span style={{ ...pillBase, top: 10, left: 10, height: 22, padding: "0 8px", background: LOCKED_INK, fontSize: 10.5, letterSpacing: "0.06em" }}>
          {example ? "EXAMPLE" : "IN-STORE"}
        </span>
        <span style={offTab}>{deal.discountBand.toUpperCase()}</span>
      </span>
      <span className="flex flex-1 flex-col gap-2.5 border-t border-dashed p-3" style={{ borderColor: HF.dashed }}>
        <span className="flex flex-col gap-[3px] px-0.5">
          <span className="line-clamp-2 h-9 text-[13px] font-semibold leading-[18px] sm:text-[13.5px]">{cleanName(deal.name)}</span>
          <span className="truncate text-[12px]" style={{ color: HF.text3 }}>
            {chain} · {example ? "example" : "near you"}
          </span>
        </span>
        <span
          className="mt-auto flex h-10 items-center justify-center gap-[7px] rounded-xl text-[13px] font-bold text-white transition-opacity group-hover:opacity-90 sm:text-[13.5px]"
          style={{ background: HF.locked }}
        >
          <LockIcon size={14} color="#FFD24A" />
          Unlock deal
        </span>
      </span>
    </Link>
  );
}

export function LockedSkeletonCard() {
  return (
    <div className={`${tagCardClass} animate-pulse`} style={{ borderColor: HF.border }} role="status" aria-label="Checking clearance near you">
      <div className="aspect-square" style={{ background: HF.skeleton }} />
      <div className="flex flex-col gap-2.5 border-t border-dashed p-3" style={{ borderColor: HF.dashed }}>
        <div className="h-9 rounded" style={{ background: HF.skeleton }} />
        <div className="h-3.5 w-24 rounded" style={{ background: HF.skeleton }} />
        <div className="h-10 rounded-xl" style={{ background: HF.skeleton }} />
      </div>
    </div>
  );
}

export function FeedSkeletonCard() {
  return (
    <div className={`${tagCardClass} animate-pulse`} style={{ borderColor: HF.border }} aria-hidden="true">
      <div className="aspect-square" style={{ background: HF.skeleton }} />
      <div className="flex flex-col gap-2 border-t border-dashed px-3 pb-3 pt-2.5" style={{ borderColor: HF.dashed }}>
        <div className="h-5 w-20 rounded" style={{ background: HF.skeleton }} />
        <div className="h-[34px] rounded" style={{ background: HF.skeleton }} />
        <div className="h-4 w-24 rounded" style={{ background: HF.skeleton }} />
      </div>
    </div>
  );
}

// ---------- list mode rows ----------

const rowClass = "flex items-center gap-3 border-b px-3 py-2.5 last:border-b-0 sm:gap-4 sm:px-4";

/** One deal as a compact row, for the list layout of the "View all" feed. */
export function ProductFeedRow({
  product,
  storeLabel,
  onOpen,
  onSave,
}: {
  product: ProductListItem;
  storeLabel: string;
  onOpen: () => void;
  onSave: () => void;
}) {
  const price = getGuestDealPrice(product);
  const discount = getGuestDealDiscount(product);
  const msrp = Number(product.msrp) || 0;
  const status = getProductStatus(product);
  const save = msrp > price && price > 0 ? msrp - price : 0;
  const [imageFailed, setImageFailed] = useState(false);
  const src = getProductImageUrl(product.image, "200");

  return (
    <article className={rowClass} style={{ borderColor: HF.divider }} data-testid="feed-row">
      <button
        type="button"
        onClick={onOpen}
        className="flex min-w-0 flex-1 items-center gap-3 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#8B5CF6] sm:gap-4"
        aria-label={`${product.name}${price > 0 ? `, ${money.format(price)}` : ""}. Open deal`}
      >
        <span className="relative flex h-16 w-16 flex-shrink-0 items-center justify-center rounded-[10px] sm:h-[72px] sm:w-[72px]" style={{ background: HF.tile }}>
          {src && !imageFailed ? (
            <img src={src} alt="" loading="lazy" onError={() => setImageFailed(true)} className="h-[84%] w-[84%] object-contain mix-blend-multiply" />
          ) : null}
        </span>
        <span className="flex min-w-0 flex-1 flex-col gap-1">
          <span className="line-clamp-2 text-[13.5px] font-medium leading-[18px] sm:text-[14px]" style={{ color: HF.ink }}>{cleanName(product.name)}</span>
          <span className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[12px]" style={{ color: HF.text3 }}>
            {status === "lowest" && <span className="rounded-[5px] px-1.5 py-px text-[10.5px] font-extrabold tracking-[0.3px] text-white" style={{ background: HF.violet }}>LOWEST</span>}
            {status === "new" && <span className="rounded-[5px] px-1.5 py-px text-[10.5px] font-extrabold tracking-[0.3px] text-white" style={{ background: "#10B981" }}>NEW</span>}
            <span className="hidden sm:inline">{storeLabel}</span>
            {save > 0 && <span className="font-semibold" style={{ color: HF.green }}>Save {money.format(save)}</span>}
          </span>
        </span>
        <span className="flex flex-shrink-0 flex-col items-end gap-0.5">
          <span className="text-[16px] font-extrabold tabular-nums sm:text-[17px]" style={{ color: HF.ink }}>{price > 0 ? money.format(price) : "See price"}</span>
          {msrp > price && price > 0 && <span className="text-[12px] tabular-nums line-through" style={{ color: HF.muted }}>{money.format(msrp)}</span>}
          {discount > 0 && <span className="rounded-[5px] px-1.5 text-[11.5px] font-bold" style={{ background: "#FFD24A", color: "#170B26" }}>{discount}% OFF</span>}
        </span>
      </button>
      <ShareDealButton product={product} storeLabel={storeLabel} size="row" />
      <button
        type="button"
        onClick={onSave}
        aria-label={`Save ${product.name}`}
        className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full border bg-[var(--hf-surface)] transition hover:border-[var(--hf-heart-border)] hover:bg-[var(--hf-heart-soft)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#8B5CF6]"
        style={{ borderColor: HF.border, color: HF.heart }}
      >
        <HeartIcon />
      </button>
    </article>
  );
}

/** A locked in-store deal in the list layout: same row shape, the price swapped for an unlock button. */
export function LockedDealRow({
  deal,
  zip,
  to,
  introLabel,
  onClick,
}: {
  deal: PublicPreviewDeal;
  zip: string;
  to: string;
  introLabel: string;
  onClick: () => void;
}) {
  const chain = localChainName(deal.retailer);
  const [imageFailed, setImageFailed] = useState(false);
  const src = getProductImageUrl(deal.image, "200");
  return (
    <Link
      to={to}
      onClick={onClick}
      className={`${rowClass} no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#8B5CF6]`}
      style={{ borderColor: HF.divider, color: HF.ink, background: HF.creamRow }}
      aria-label={`In-store clearance near ${zip}: ${deal.name}, ${deal.discountBand}. Unlock for ${introLabel} first month`}
      data-testid="locked-deal-row"
    >
      <span className="relative flex h-16 w-16 flex-shrink-0 items-center justify-center rounded-[10px] bg-[var(--hf-photo)] sm:h-[72px] sm:w-[72px]">
        {src && !imageFailed ? (
          <img src={src} alt="" loading="lazy" onError={() => setImageFailed(true)} className="h-[84%] w-[84%] object-contain" />
        ) : null}
      </span>
      <span className="flex min-w-0 flex-1 flex-col gap-1">
        <span className="line-clamp-2 text-[13.5px] font-medium leading-[18px] sm:text-[14px]">{cleanName(deal.name)}</span>
        <span className="flex flex-wrap items-center gap-x-2 text-[12px]" style={{ color: HF.text3 }}>
          <span className="rounded-[5px] px-1.5 py-px text-[10.5px] font-extrabold tracking-[0.3px] text-white" style={{ background: LOCKED_INK }}>IN-STORE</span>
          <span>{chain} · near you</span>
          <span className="font-bold" style={{ color: HF.amberInk }}>{deal.discountBand}</span>
        </span>
      </span>
      <span className="flex h-9 flex-shrink-0 items-center gap-1.5 rounded-[10px] px-3 text-[12.5px] font-bold text-white" style={{ background: HF.locked }}>
        <LockIcon size={12} color="#FFD24A" />
        <span className="hidden sm:inline">Unlock deal</span>
        <span className="sm:hidden">Unlock</span>
      </span>
    </Link>
  );
}

export function FeedSkeletonRow() {
  return (
    <div className={`${rowClass} animate-pulse`} style={{ borderColor: HF.divider }} aria-hidden="true">
      <div className="h-16 w-16 flex-shrink-0 rounded-[10px] sm:h-[72px] sm:w-[72px]" style={{ background: HF.skeleton }} />
      <div className="flex flex-1 flex-col gap-2">
        <div className="h-4 w-3/4 rounded" style={{ background: HF.skeleton }} />
        <div className="h-3 w-1/3 rounded" style={{ background: HF.skeleton }} />
      </div>
      <div className="h-5 w-14 rounded" style={{ background: HF.skeleton }} />
    </div>
  );
}

export function ZipPromptCard({
  initialZip = "",
  error,
  introLabel,
  onSubmit,
}: {
  initialZip?: string;
  error?: string;
  introLabel: string;
  onSubmit: (zip: string) => void;
}) {
  const [zip, setZip] = useState(initialZip);
  const [invalid, setInvalid] = useState(false);
  const submit = (event: FormEvent) => {
    event.preventDefault();
    const value = zip.trim();
    if (!/^\d{5}$/.test(value)) {
      setInvalid(true);
      return;
    }
    setInvalid(false);
    onSubmit(value);
  };
  const message = invalid ? "Enter a 5-digit ZIP code" : error;

  return (
    <form onSubmit={submit} className={cardClass} style={lockedCardStyle} aria-label="Find clearance near you" data-testid="zip-prompt-card">
      <div
        className="relative flex aspect-square flex-col items-center justify-center gap-2 overflow-hidden rounded-[16px] px-3 pt-7 text-center"
        style={{ background: HF.cream }}
      >
        <span style={{ ...pillBase, top: 8, left: 8, background: HF.surface, color: HF.amberText, borderRadius: 6, height: 20, fontSize: 10.5 }}>IN-STORE</span>
        <span className="hidden h-11 w-11 items-center justify-center rounded-full bg-[var(--hf-surface)] sm:flex">
          <LockIcon size={20} color={HF.amberText} />
        </span>
        <span className="text-[17px] font-extrabold leading-[21px] sm:text-lg" style={{ color: HF.ink }}>
          What’s on clearance near you?
        </span>
        <span className="text-[12.5px] leading-4" style={{ color: HF.text3 }}>
          In-store markdowns at the stores in your ZIP
        </span>
      </div>
      <label className="sr-only" htmlFor="home-feed-zip">ZIP code</label>
      <input
        id="home-feed-zip"
        value={zip}
        onChange={(e) => setZip(e.target.value.replace(/\D/g, "").slice(0, 5))}
        inputMode="numeric"
        autoComplete="postal-code"
        placeholder="ZIP code"
        maxLength={5}
        aria-invalid={Boolean(message) || undefined}
        aria-describedby={message ? "home-feed-zip-msg" : undefined}
        className="mt-0.5 h-[38px] w-full rounded-[10px] bg-[var(--hf-surface)] px-3 text-[15px] font-semibold tracking-wide outline-none placeholder:text-[var(--hf-placeholder)] focus:ring-2 focus:ring-[#FDE68A]"
        style={{ border: `1px solid ${HF.amberBorder}`, color: HF.ink }}
      />
      {message && (
        <span id="home-feed-zip-msg" role="alert" className="px-1 text-[12px] font-semibold" style={{ color: HF.red }}>
          {message}
        </span>
      )}
      <button
        type="submit"
        className="flex h-[34px] items-center justify-center rounded-[10px] text-[13.5px] font-bold text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D97706] focus-visible:ring-offset-1"
        style={{ background: HF.brand }}
      >
        Show me
      </button>
      <span className="px-1 pb-0.5 text-center text-[11.5px]" style={{ color: HF.text3 }}>
        Plans from {introLabel} first month
      </span>
    </form>
  );
}

/**
 * Full-width, calm empty state for a chain's in-store row when the shopper's ZIP has nothing
 * (or the check failed): neutral surface, no error colours, ZIP re-check inline, plans link.
 */
export function InstoreEmptyState({
  title,
  message,
  initialZip,
  plansTo,
  onPlansClick,
  onSubmit,
}: {
  title: string;
  message: string;
  initialZip: string;
  plansTo: string;
  onPlansClick?: () => void;
  onSubmit: (zip: string) => void;
}) {
  const [zip, setZip] = useState(initialZip);
  const [invalid, setInvalid] = useState(false);
  const submit = (event: FormEvent) => {
    event.preventDefault();
    const value = zip.trim();
    if (!/^\d{5}$/.test(value)) {
      setInvalid(true);
      return;
    }
    setInvalid(false);
    onSubmit(value);
  };

  return (
    <div
      className="flex flex-col gap-4 rounded-[18px] border p-4 sm:p-5 lg:flex-row lg:items-center lg:gap-8"
      style={{ background: HF.surface2, borderColor: HF.border }}
      data-testid="instore-empty-state"
    >
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <p className="m-0 text-[15.5px] font-bold leading-[21px] sm:text-[16.5px]" style={{ color: HF.ink }}>{title}</p>
        <p className="m-0 text-[13.5px] leading-[19px] sm:text-[14px] sm:leading-5" style={{ color: HF.text3 }}>{message}</p>
      </div>
      <div className="flex flex-col gap-1.5 lg:flex-shrink-0">
      <div className="flex flex-col gap-2.5 sm:flex-row sm:items-center">
        <form onSubmit={submit} className="flex flex-col gap-2 sm:flex-row sm:items-center" aria-label="Check another ZIP code">
          <label className="sr-only" htmlFor="instore-empty-zip">ZIP code</label>
          <input
            id="instore-empty-zip"
            value={zip}
            onChange={(e) => {
              setZip(e.target.value.replace(/\D/g, "").slice(0, 5));
              setInvalid(false);
            }}
            inputMode="numeric"
            autoComplete="postal-code"
            placeholder="ZIP code"
            maxLength={5}
            aria-invalid={invalid || undefined}
            aria-describedby={invalid ? "instore-empty-zip-hint" : undefined}
            className="h-10 w-full rounded-[10px] border bg-[var(--hf-surface)] px-3 text-[15px] font-semibold tracking-wide outline-none placeholder:text-[var(--hf-placeholder)] focus:border-[var(--hf-violet-border)] focus:ring-2 focus:ring-[var(--hf-violet-soft)] sm:w-[128px]"
            style={{ borderColor: HF.chipBorder, color: HF.ink }}
          />
          <button
            type="submit"
            className="h-10 flex-shrink-0 rounded-[10px] px-4 text-[14px] font-bold transition hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#8B5CF6] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--hf-surface-2)]"
            style={{ background: HF.inkBtn, color: HF.onInkBtn }}
          >
            Check ZIP
          </button>
        </form>
        <Link
          to={plansTo}
          onClick={onPlansClick}
          className="flex h-10 items-center justify-center whitespace-nowrap rounded-[10px] px-2 text-[14px] font-bold no-underline hover:underline sm:justify-start"
          style={{ color: HF.brandText }}
        >
          See plans
        </Link>
      </div>
      {invalid && (
        <span id="instore-empty-zip-hint" role="status" className="text-[12.5px]" style={{ color: HF.text3 }}>
          Enter a 5-digit ZIP code
        </span>
      )}
      </div>
    </div>
  );
}

/** Slim in-store upsell strip above the feed, with real thumbnails of what is locked nearby. */
export function InstoreBanner({
  images,
  introLabel,
  priceLabel,
  to,
  onClick,
}: {
  images: string[];
  introLabel: string;
  priceLabel: string;
  to: string;
  onClick: () => void;
}) {
  const thumbs = images.filter(Boolean).slice(0, 3);
  return (
    <Link
      to={to}
      onClick={onClick}
      data-testid="instore-banner"
      className="group mb-4 flex items-center gap-3 rounded-[18px] px-3 py-3 no-underline shadow-[0_6px_20px_rgba(59,8,95,0.18)] transition hover:shadow-[0_10px_28px_rgba(59,8,95,0.28)] sm:gap-4 sm:px-4"
      style={{ background: HF.brand, color: "#FFFFFF" }}
      aria-label={`In-store clearance near you, 70% or more off. Unlock for ${introLabel} first month, then ${priceLabel} per month.`}
    >
      {thumbs.length > 0 ? (
        <span className="flex flex-shrink-0" aria-hidden="true">
          {thumbs.map((src, index) => (
            <span
              key={src + index}
              className={`${index === 2 ? "hidden sm:flex" : "flex"} h-10 w-10 items-center justify-center overflow-hidden rounded-full bg-white ring-2 sm:h-11 sm:w-11`}
              style={{ marginLeft: index === 0 ? 0 : -12, zIndex: 3 - index, ["--tw-ring-color" as string]: HF.brand }}
            >
              <img src={src} alt="" className="h-[82%] w-[82%] object-contain" loading="lazy" />
            </span>
          ))}
        </span>
      ) : (
        <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full" style={{ background: "rgba(255,255,255,0.12)" }} aria-hidden="true">
          <LockIcon size={17} color="#FCD34D" />
        </span>
      )}
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="hidden items-center gap-1.5 text-[10.5px] font-bold uppercase tracking-[0.9px] sm:flex" style={{ color: "#FCD34D" }}>
          <LockIcon size={10} color="#FCD34D" />
          In-store clearance
        </span>
        <span className="text-[14px] font-semibold leading-[18px] sm:text-[15.5px] sm:leading-5">
          <span className="sm:hidden">In-store clearance near you, 70%+ off</span>
          <span className="hidden sm:inline">70%+ off at Walmart and HD near you</span>
        </span>
        <span className="hidden text-[12.5px] sm:block" style={{ color: "#D8C8EE" }}>
          Members see the shelf price, aisle and stock · {introLabel} first month, then {priceLabel}/mo
        </span>
      </span>
      <span className="flex h-9 flex-shrink-0 items-center gap-1 rounded-full bg-white px-3.5 text-[13.5px] font-bold transition group-hover:bg-[#F3EEFC] sm:h-10 sm:px-4 sm:text-[14px]" style={{ color: HF.brand }}>
        <span className="hidden sm:inline">Unlock</span> {introLabel}
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M9 6l6 6-6 6" /></svg>
      </span>
    </Link>
  );
}

/** Closes the in-store row: there is more on the shelves than the preview shows. */
export function MoreInstoreCard({ zip, to, onClick }: { zip: string; to: string; onClick: () => void }) {
  return (
    <Link
      to={to}
      onClick={onClick}
      className="flex aspect-square flex-col items-center justify-center gap-1.5 rounded-[16px] border-[1.5px] border-dashed px-3 text-center no-underline transition hover:bg-[var(--hf-cream-hover)]"
      style={{ borderColor: HF.creamBorder, color: HF.creamText }}
      data-testid="more-instore-card"
    >
      <span className="text-[14px] font-bold" style={{ color: HF.ink }}>+ more on your shelves</span>
      <span className="text-[12.5px] leading-[17px]">Unlock to see every clearance item near {zip}</span>
    </Link>
  );
}
